import { FilterGrid } from '@theme/fifi-filter-grid';
import { DialogComponent } from '@theme/dialog';

/**
 * The V3 inspiration wall (sections/fifi-inspiration.liquid, design-handoff/design/Inspiration V3.dc.html):
 * the theme chips, count and "Show more" from FilterGrid, plus the lightbox.
 *
 * Tiles call `on:click="/open"`. Each carries its lightbox content, written in Liquid:
 * `data-src`, `data-srcset`, `data-width`, `data-height`, `data-title`, `data-eyebrow` (the theme)
 * and `data-href` (the product, when there is one). The lightbox (`ref="lightbox"`, Horizon's
 * <dialog-component>) browses the tiles matching the chosen chip with its arrow buttons
 * (`on:click="fifi-inspiration-wall/previous"` / `next`) and the ← → keys; Esc and the backdrop
 * close it.
 */
class InspirationWall extends FilterGrid {
  /** Position of the open tile among the current matches. */
  #index = -1;

  /** @returns {DialogComponent | null} */
  get #lightbox() {
    const { lightbox } = this.refs;
    return lightbox instanceof DialogComponent ? lightbox : null;
  }

  /** @param {Event} event */
  open(event) {
    const tile = event.target instanceof Element ? event.target.closest('[data-filter-item]') : null;
    if (!(tile instanceof HTMLElement)) return;

    this.#index = this.matchingItems.indexOf(tile);
    this.#render();
    this.#lightbox?.showDialog();
  }

  previous() {
    this.#step(-1);
  }

  next() {
    this.#step(1);
  }

  /** @param {KeyboardEvent} event */
  onKey(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }

  /** @param {number} direction */
  #step(direction) {
    const count = this.matchingItems.length;
    if (!count) return;

    this.#index = (this.#index + direction + count) % count;
    this.#render();
  }

  /** Copies the current tile's content into the lightbox. */
  #render() {
    const lightbox = this.#lightbox;
    const matches = this.matchingItems;
    const tile = matches[this.#index];
    if (!lightbox || !tile) return;

    const { src = '', srcset = '', width, height, title = '', eyebrow = '', href = '' } = tile.dataset;

    const image = lightbox.querySelector('[data-lightbox-image]');
    if (image instanceof HTMLImageElement) {
      image.srcset = srcset;
      image.src = src;
      image.alt = title;
      if (width && height) {
        image.width = Number(width);
        image.height = Number(height);
      }
    }

    const eyebrowElement = lightbox.querySelector('[data-lightbox-eyebrow]');
    if (eyebrowElement instanceof HTMLElement) {
      eyebrowElement.textContent = eyebrow;
      eyebrowElement.hidden = !eyebrow;
    }

    const titleElement = lightbox.querySelector('[data-lightbox-title]');
    if (titleElement) titleElement.textContent = title;

    const order = lightbox.querySelector('[data-lightbox-order]');
    if (order instanceof HTMLAnchorElement) {
      if (href) order.href = href;
      order.hidden = !href;
    }

    const counter = lightbox.querySelector('[data-lightbox-counter]');
    if (counter instanceof HTMLElement) {
      counter.textContent = (counter.dataset.template ?? '')
        .replace('__INDEX__', String(this.#index + 1))
        .replace('__TOTAL__', String(matches.length));
    }
  }
}

if (!customElements.get('fifi-inspiration-wall')) {
  customElements.define('fifi-inspiration-wall', InspirationWall);
}
